import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Key, User, Camera, MessageSquareCode, SlidersHorizontal, LogOut, Lock, UserCog, Plus, Trash2, Loader2, PlugZap, ServerCog, Cpu } from 'lucide-react';
import { MAX_CUSTOM_INSTRUCTION_CHARS } from '@realplc/types';
import { CUSTOM_AI_PROVIDER } from '../constants';
import { getLanguagePreference, persistLanguagePreference, useTranslation } from '../i18n';
import { discoverModelConnectionModels, testModelConnection } from '../services/geminiService';
import {
  type CustomModelConnection,
  type CustomProviderModel,
  createBlankCustomConnection,
  createBlankCustomModel,
  generateEntryId,
  getCatalogModels,
  getCatalogProviders,
  getCustomModelConnections,
  getCustomModelSelectionId,
  getCustomProviderNameHistory,
  getKnownProviders,
  normalizeProvider,
  resolveModelSelection,
  saveCustomModelConnections,
  saveCustomModelSelectionId,
  saveCustomProviderNameToHistory,
} from '../utils/aiModelSettings';
import { useChatCatalogStore } from '../stores/chatCatalogStore';
import { getItem, setItem } from '../utils/scopedSettingsStorage';
import { useSettingsStore } from '../stores/settingsStore';
import { requestOpenTiaAgentSettings } from '../services/tiaAgentRelease';
import { requestOpenCodesysAgentSettings } from '../services/codesysAgent';
import {
  loadRealPlcAgentIntegrationStatus,
  type AgentConnectorStatus,
  type RealPlcAgentIntegrationStatus,
} from '../services/realplcAgent';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout?: () => void;
  onOpenAccount?: (section?: 'profile' | 'security') => void;
  authUser?: {
    username?: string;
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
  } | null;
}

type SettingsSectionId = 'profile' | 'ai' | 'prompting' | 'agent' | 'preferences';
type ConnectionState = { tone: 'idle' | 'success' | 'error'; message: string };

function isEmailLike(value?: string): boolean {
  return Boolean(value?.includes('@'));
}

function isPlanLikeName(value?: string): boolean {
  return /^vip[\s_-]*\d+$/i.test((value || '').trim());
}

function getEmailPrefix(email?: string): string {
  return (email || '').split('@')[0]?.trim() || '';
}

function getPreferredDisplayName(authUser?: SettingsModalProps['authUser']): string {
  const accountName = authUser?.name?.trim();
  if (accountName && !isEmailLike(accountName) && !isPlanLikeName(accountName)) {
    return accountName;
  }
  return getEmailPrefix(authUser?.email) || authUser?.phone?.trim() || '';
}

function getRoleLabel(role: string | undefined, t: (key: string) => string): string {
  if (role === 'super_admin') return t('Platform Admin');
  if (role === 'admin') return t('Organization Admin');
  if (role === 'user') return t('Authenticated User');
  return t('Viewer');
}

function formatCustomConnectionLabel(connection: CustomModelConnection, fallback: string): string {
  return connection.name || connection.baseUrl || fallback;
}

function connectorStatusLabel(connector: AgentConnectorStatus | undefined, t: (key: string) => string): string {
  if (!connector) return t('Unknown');
  const labels: Record<string, string> = {
    not_installed: t('Not installed'),
    disabled: t('Disabled'),
    detecting: t('Detecting'),
    setup_required: t('Setup required'),
    ready: t('Ready'),
    busy: t('Busy'),
    degraded: t('Degraded'),
    quarantined: t('Quarantined'),
    update_required: t('Update required'),
    waiting_for_user_session: t('Waiting for user session'),
  };
  return labels[connector.state] ?? connector.state;
}

function connectorProductLabel(connector: AgentConnectorStatus | undefined, fallback: string): string {
  const product = connector?.detectedProducts?.[0];
  if (!product) return fallback;
  const name = String(product.product ?? product.profileName ?? fallback);
  const version = String(product.version ?? '').trim();
  return version ? `${name} ${version}` : name;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, onLogout, onOpenAccount, authUser }) => {
  const { t, language } = useTranslation();
  const [provider, setProvider] = useState('realplc');
  const [model, setModel] = useState('autofree');
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  const [baseUrls, setBaseUrls] = useState<Record<string, string>>({});
  const [theme, setTheme] = useState('Dark');
  const [uiLanguage, setUiLanguage] = useState('System');
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [customConnections, setCustomConnections] = useState<CustomModelConnection[]>([]);
  const [selectedCustomConnectionId, setSelectedCustomConnectionId] = useState('');
  const [selectedCustomModelId, setSelectedCustomModelId] = useState('');
  const [providerNameHistory, setProviderNameHistory] = useState<string[]>([]);
  const [settingsError, setSettingsError] = useState('');
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isFetchingModels, setIsFetchingModels] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>({ tone: 'idle', message: '' });
  const [discoveredModels, setDiscoveredModels] = useState<CustomProviderModel[]>([]);
  const modelListRef = useRef<HTMLDivElement>(null);
  const restoreModelAfterCatalogLoad = useRef(false);
  const planAutoApprovalStore = useSettingsStore(s => s.planAutoApproval);
  const setPlanAutoApprovalStore = useSettingsStore(s => s.setPlanAutoApproval);
  const [planAutoApprovalEnabled, setPlanAutoApprovalEnabled] = useState(planAutoApprovalStore.enabled);
  const [planAutoApprovalSeconds, setPlanAutoApprovalSeconds] = useState(planAutoApprovalStore.seconds);
  const [tiaAgentEnabled, setTiaAgentEnabled] = useState(true);
  const [codesysAgentEnabled, setCodesysAgentEnabled] = useState(true);
  const [agentIntegrationStatus, setAgentIntegrationStatus] = useState<RealPlcAgentIntegrationStatus | null>(null);
  const [agentIntegrationLoading, setAgentIntegrationLoading] = useState(false);
  const [agentIntegrationError, setAgentIntegrationError] = useState('');
  const [activeSection, setActiveSection] = useState<SettingsSectionId>('profile');
  const isSuperAdmin = authUser?.role === 'super_admin';
  // Both settings selectors use the current user's catalog visibility.
  const catalog = useChatCatalogStore((s) => s.catalog);
  const catalogLoadState = useChatCatalogStore((s) => s.loadState);
  const effectiveProviders = useMemo(() => getCatalogProviders({ isSuperAdmin }), [isSuperAdmin, catalog]);
  const effectiveModels = useMemo(() => getCatalogModels({ isSuperAdmin }), [isSuperAdmin, catalog]);

  useEffect(() => {
    if (isOpen) {
      restoreModelAfterCatalogLoad.current = !catalog;
      void useChatCatalogStore.getState().load();
      const catalogModels = getCatalogModels({ isSuperAdmin });
      const savedProvider = normalizeProvider(getItem('ai_provider') || effectiveProviders[0].value);
      const savedModel = getItem('ai_model') || catalogModels.find((m) => m.provider === savedProvider)?.value || catalogModels[0].value;
      const savedModelObj = catalogModels.find((m) => m.provider === savedProvider && m.value === savedModel)
        || catalogModels.find((m) => m.value === savedModel);
      const activeProviderRaw = savedModelObj?.provider || savedProvider;
      const activeProvider = effectiveProviders.some((p) => p.value === activeProviderRaw)
        ? activeProviderRaw
        : effectiveProviders[0].value;
      const savedCustomConnections = getCustomModelConnections();
      const selectedCustomId = getCustomModelSelectionId(savedProvider === CUSTOM_AI_PROVIDER ? savedModel : undefined);
      const selectedCustomConnection = savedCustomConnections.find((connection) =>
        connection.models.some((item) => item.id === selectedCustomId)
      ) || savedCustomConnections[0];
      const selectedCustomModel = selectedCustomConnection?.models.find((item) => item.id === selectedCustomId)
        || selectedCustomConnection?.models[0];
      const activeModel = activeProvider === CUSTOM_AI_PROVIDER
        ? selectedCustomModel?.id || selectedCustomId
        : (savedModelObj?.provider === activeProvider ? savedModelObj.value : undefined)
          || catalogModels.find((m) => m.provider === activeProvider)?.value || catalogModels[0].value;
      const savedTheme = getItem('ui_theme') || 'Dark';
      const savedUiLanguage = getLanguagePreference();
      const userKey = authUser?.email || 'default';
      const preferredDisplayName = getPreferredDisplayName(authUser);
      const savedUserName = localStorage.getItem(`user_name_${userKey}`);
      const displayName = savedUserName && !isEmailLike(savedUserName) && !isPlanLikeName(savedUserName)
        ? savedUserName
        : preferredDisplayName;
      const savedUserAvatar = localStorage.getItem(`user_avatar_${userKey}`) || '';
      const savedCustomInstructions = getItem('custom_instructions') || '';

      const keys: Record<string, string> = {};
      const urls: Record<string, string> = {};
      getKnownProviders().forEach((p) => {
        keys[p.value] = getItem(`${p.value}_api_key`) || (p.value === 'aliyun' ? getItem('qwen_api_key') : null) || '';
        urls[p.value] = getItem(`${p.value}_base_url`) || (p.value === 'aliyun' ? getItem('qwen_base_url') : null) || p.baseUrl || '';
      });

      if (getItem('ai_provider') === 'qwen') {
        setItem('ai_provider', 'aliyun');
      }
      if (getItem('ai_provider') === 'shengong') {
        setItem('ai_provider', 'realplc');
      }
      if (getItem('shengong_api_key')) {
        setItem('realplc_api_key', getItem('shengong_api_key'));
      }
      if (getItem('shengong_base_url')) {
        setItem('realplc_base_url', getItem('shengong_base_url'));
      }

      setProvider(activeProvider);
      setModel(activeModel);
      setApiKeys(keys);
      setBaseUrls(urls);
      setTheme(savedTheme);
      setUiLanguage(savedUiLanguage);
      setUserName(displayName);
      setUserAvatar(savedUserAvatar);
      setCustomInstructions(savedCustomInstructions);
      setCustomConnections(savedCustomConnections);
      setSelectedCustomConnectionId(selectedCustomConnection?.id || '');
      setSelectedCustomModelId(selectedCustomModel?.id || selectedCustomId);
      setProviderNameHistory(getCustomProviderNameHistory());
      setSettingsError('');
      setConnectionState({ tone: 'idle', message: '' });
      setIsFetchingModels(false);
      setDiscoveredModels([]);
      setTiaAgentEnabled(getItem('agent_tia_enabled') !== 'false');
      setCodesysAgentEnabled(getItem('agent_codesys_enabled') !== 'false');
      setActiveSection('profile');
    }
  }, [authUser, isOpen]);

  // The catalog may answer after the modal opened. Re-validate the selection
  // against it and add credential slots for providers the built-in list did
  // not know — without touching anything already typed, which is why this is
  // not folded into the effect above that resets the whole form.
  useEffect(() => {
    if (!isOpen) return;
    let nextProvider = provider;
    let nextModel = model;
    if (restoreModelAfterCatalogLoad.current && catalog) {
      restoreModelAfterCatalogLoad.current = false;
      // A saved catalog model cannot be resolved until its provider has loaded.
      if (getItem('ai_provider') !== CUSTOM_AI_PROVIDER) {
        nextProvider = getItem('ai_provider') || provider;
        nextModel = getItem('ai_model') || model;
      }
    }
    if (nextProvider !== CUSTOM_AI_PROVIDER) {
      const resolved = resolveModelSelection(nextProvider, nextModel, { isSuperAdmin });
      if (resolved.provider !== provider) setProvider(resolved.provider);
      if (resolved.model !== model) setModel(resolved.model);
    }
    if (catalogLoadState !== 'ready') return;
    const fillMissing = (suffix: 'api_key' | 'base_url') => (current: Record<string, string>) => {
      const next = { ...current };
      for (const p of getKnownProviders()) {
        if (next[p.value] !== undefined) continue;
        next[p.value] = getItem(`${p.value}_${suffix}`) || (suffix === 'base_url' ? p.baseUrl : '') || '';
      }
      return next;
    };
    setApiKeys(fillMissing('api_key'));
    setBaseUrls(fillMissing('base_url'));
  }, [isOpen, catalog, catalogLoadState, isSuperAdmin, provider, model]);

  useEffect(() => {
    if (!isOpen || activeSection !== 'agent') return;
    let cancelled = false;
    setAgentIntegrationLoading(true);
    setAgentIntegrationError('');
    void loadRealPlcAgentIntegrationStatus()
      .then((status) => {
        if (!cancelled) setAgentIntegrationStatus(status);
      })
      .catch((error) => {
        if (!cancelled) {
          setAgentIntegrationStatus(null);
          setAgentIntegrationError(error instanceof Error ? error.message : t('Failed to load RealPLC Agent status'));
        }
      })
      .finally(() => {
        if (!cancelled) setAgentIntegrationLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeSection, isOpen, t]);

  const activeCustomConnection = customConnections.find((connection) => connection.id === selectedCustomConnectionId)
    || customConnections[0];
  const activeCustomModel = activeCustomConnection?.models.find((item) => item.id === selectedCustomModelId)
    || activeCustomConnection?.models[0];

  const updateActiveCustomConnection = (updates: Partial<CustomModelConnection>) => {
    if (!activeCustomConnection) return;
    setCustomConnections((connections) => connections.map((connection) => (
      connection.id === activeCustomConnection.id ? { ...connection, ...updates } : connection
    )));
    setConnectionState({ tone: 'idle', message: '' });
  };

  const updateActiveCustomModel = (updates: Partial<CustomProviderModel>) => {
    if (!activeCustomConnection || !activeCustomModel) return;
    setCustomConnections((connections) => connections.map((connection) => (
      connection.id === activeCustomConnection.id
        ? {
          ...connection,
          models: connection.models.map((item) => (
            item.id === activeCustomModel.id ? { ...item, ...updates } : item
          )),
        }
        : connection
    )));
    setConnectionState({ tone: 'idle', message: '' });
  };

  const handleAddCustomConnection = () => {
    const connection = createBlankCustomConnection();
    const modelId = connection.models[0].id;
    setCustomConnections((connections) => [...connections, connection]);
    setSelectedCustomConnectionId(connection.id);
    setSelectedCustomModelId(modelId);
    setProvider(CUSTOM_AI_PROVIDER);
    setModel(modelId);
    setSettingsError('');
    setConnectionState({ tone: 'idle', message: '' });
    setDiscoveredModels([]);
  };

  const handleDeleteCustomConnection = () => {
    if (!activeCustomConnection) return;
    const remaining = customConnections.filter((connection) => connection.id !== activeCustomConnection.id);
    const nextConnections = remaining.length > 0 ? remaining : [createBlankCustomConnection()];
    const nextModel = nextConnections[0].models[0];
    setCustomConnections(nextConnections);
    setSelectedCustomConnectionId(nextConnections[0].id);
    setSelectedCustomModelId(nextModel.id);
    setModel(nextModel.id);
    setSettingsError('');
    setConnectionState({ tone: 'idle', message: '' });
    setDiscoveredModels([]);
  };

  const handleAddCustomModel = () => {
    if (!activeCustomConnection) return;
    const nextModel = createBlankCustomModel();
    setCustomConnections((connections) => connections.map((connection) => (
      connection.id === activeCustomConnection.id
        ? { ...connection, models: [...connection.models, nextModel] }
        : connection
    )));
    setSelectedCustomModelId(nextModel.id);
    setModel(nextModel.id);
    setSettingsError('');
    setConnectionState({ tone: 'idle', message: '' });
    // Scroll to bottom after DOM update
    setTimeout(() => {
      modelListRef.current?.scrollTo({ top: modelListRef.current.scrollHeight, behavior: 'smooth' });
    }, 50);
  };

  const handleDeleteCustomModel = (modelId?: string) => {
    if (!activeCustomConnection) return;
    const targetId = modelId || activeCustomModel?.id;
    if (!targetId) return;
    const remainingModels = activeCustomConnection.models.filter((item) => item.id !== targetId);
    const nextModels = remainingModels.length > 0 ? remainingModels : [createBlankCustomModel()];
    setCustomConnections((connections) => connections.map((connection) => (
      connection.id === activeCustomConnection.id ? { ...connection, models: nextModels } : connection
    )));
    // Only switch selection if the deleted model is the currently selected one
    if (targetId === activeCustomModel?.id) {
      setSelectedCustomModelId(nextModels[0].id);
      setModel(nextModels[0].id);
    }
    setSettingsError('');
    setConnectionState({ tone: 'idle', message: '' });
  };

  const toggleModelEnabled = (modelId: string) => {
    if (!activeCustomConnection) return;
    setCustomConnections((connections) => connections.map((connection) => (
      connection.id === activeCustomConnection.id
        ? {
          ...connection,
          models: connection.models.map((item) => (
            item.id === modelId ? { ...item, enabled: !item.enabled } : item
          )),
        }
        : connection
    )));
  };

  const handleAddDiscoveredModel = (model: CustomProviderModel) => {
    if (!activeCustomConnection) return;
    setCustomConnections((connections) => connections.map((connection) => (
      connection.id === activeCustomConnection.id
        ? { ...connection, models: [...connection.models, model] }
        : connection
    )));
    setDiscoveredModels((prev) => prev.filter((m) => m.id !== model.id));
    setSelectedCustomModelId(model.id);
    setModel(model.id);
    setConnectionState({ tone: 'idle', message: '' });
  };

  const handleAddAllDiscoveredModels = () => {
    if (!activeCustomConnection || discoveredModels.length === 0) return;
    setCustomConnections((connections) => connections.map((connection) => (
      connection.id === activeCustomConnection.id
        ? { ...connection, models: [...connection.models, ...discoveredModels] }
        : connection
    )));
    const lastModel = discoveredModels[discoveredModels.length - 1];
    setSelectedCustomModelId(lastModel.id);
    setModel(lastModel.id);
    setDiscoveredModels([]);
    setConnectionState({ tone: 'idle', message: '' });
  };

  const handleSave = () => {
    const userKey = authUser?.email || 'default';
    const normalizedCustomConnections = customConnections
      .map((connection) => ({
        ...connection,
        name: connection.name.trim(),
        apiKey: connection.apiKey.trim(),
        baseUrl: connection.baseUrl.trim(),
        enabled: connection.enabled !== false,
        models: connection.models
          .map((item) => ({
            ...item,
            modelId: item.modelId.trim(),
            displayName: item.displayName?.trim() || '',
            enabled: item.enabled !== false,
            source: item.source || 'manual' as const,
          }))
          .filter((item) => item.id === selectedCustomModelId || item.modelId || item.displayName),
      }))
      .filter((connection) => (
        connection.id === selectedCustomConnectionId
        || connection.name
        || connection.apiKey
        || connection.baseUrl
        || connection.models.length > 0
      ));
    const activeConnection = normalizedCustomConnections.find((connection) => connection.id === selectedCustomConnectionId)
      || normalizedCustomConnections[0];
    const activeModel = activeConnection?.models.find((item) => item.id === selectedCustomModelId)
      || activeConnection?.models[0];

    if (provider === CUSTOM_AI_PROVIDER) {
      if (!activeConnection) {
        setSettingsError(t('Add a custom provider before saving.'));
        setActiveSection('ai');
        return;
      }
      if (!activeModel?.modelId) {
        setSettingsError(t('Enter a model name before saving.'));
        setActiveSection('ai');
        return;
      }
      if (!activeConnection.baseUrl) {
        setSettingsError(t('Enter a Base URL before saving.'));
        setActiveSection('ai');
        return;
      }
    }

    setSettingsError('');
    setItem('ai_provider', provider);
    setItem('ai_model', provider === CUSTOM_AI_PROVIDER && activeModel ? activeModel.modelId : model);
    setItem('ui_theme', theme);
    setPlanAutoApprovalStore({ enabled: planAutoApprovalEnabled, seconds: planAutoApprovalSeconds });
    setItem('agent_tia_enabled', tiaAgentEnabled ? 'true' : 'false');
    setItem('agent_codesys_enabled', codesysAgentEnabled ? 'true' : 'false');
    persistLanguagePreference(uiLanguage, uiLanguage === 'System' ? 'auto' : 'manual');
    localStorage.setItem(`user_name_${userKey}`, userName);
    localStorage.setItem(`user_avatar_${userKey}`, userAvatar);
    setItem('custom_instructions', customInstructions);
    Object.entries(apiKeys).forEach(([prov, key]) => {
      setItem(`${prov}_api_key`, key);
    });
    Object.entries(baseUrls).forEach(([prov, url]) => {
      setItem(`${prov}_base_url`, url);
    });
    saveCustomModelConnections(normalizedCustomConnections);
    normalizedCustomConnections.forEach((connection) => saveCustomProviderNameToHistory(connection.name));
    if (provider === CUSTOM_AI_PROVIDER && activeConnection && activeModel) {
      setItem('ai_model', activeModel.modelId);
      saveCustomModelSelectionId(activeModel.id);
      setItem('custom_model_id', activeModel.modelId);
      setItem('custom_model_label', activeModel.displayName || activeModel.modelId);
      setItem('custom_model_provider_label', activeConnection.name);
      setItem('custom_api_key', activeConnection.apiKey);
      setItem('custom_base_url', activeConnection.baseUrl);
      setProviderNameHistory(getCustomProviderNameHistory());
    }
    window.dispatchEvent(new Event('settings_updated'));
    onClose();
  };

  const handleKeyChange = (prov: string, value: string) => {
    setApiKeys((prev) => ({ ...prev, [prov]: value }));
  };

  const handleUrlChange = (prov: string, value: string) => {
    setBaseUrls((prev) => ({ ...prev, [prov]: value }));
  };

  const handleTestConnection = async () => {
    const connectionModel = provider === CUSTOM_AI_PROVIDER ? activeCustomModel?.modelId.trim() || '' : model;
    const baseUrl = provider === CUSTOM_AI_PROVIDER ? activeCustomConnection?.baseUrl.trim() || '' : baseUrls[provider]?.trim() || '';
    const apiKey = provider === CUSTOM_AI_PROVIDER ? activeCustomConnection?.apiKey.trim() || '' : apiKeys[provider]?.trim() || '';

    if (!connectionModel) {
      setConnectionState({ tone: 'error', message: t('Enter a model name before testing.') });
      return;
    }
    if (provider !== 'realplc' && !baseUrl) {
      setConnectionState({ tone: 'error', message: t('Enter a Base URL before testing.') });
      return;
    }

    setIsTestingConnection(true);
    setConnectionState({ tone: 'idle', message: '' });
    try {
      const result = await testModelConnection({
        provider,
        model: connectionModel,
        apiKey,
        baseUrl,
      });
      setConnectionState({
        tone: result.ok ? 'success' : 'error',
        message: result.message || (result.ok ? t('Connection succeeded') : t('Connection failed')),
      });
    } catch (error) {
      setConnectionState({
        tone: 'error',
        message: error instanceof Error ? error.message : t('Connection failed'),
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleFetchCustomModels = async () => {
    if (!activeCustomConnection) return;
    const baseUrl = activeCustomConnection.baseUrl.trim();
    const apiKey = activeCustomConnection.apiKey.trim();

    if (!baseUrl) {
      setConnectionState({ tone: 'error', message: t('Enter a Base URL before fetching models.') });
      return;
    }

    setIsFetchingModels(true);
    setConnectionState({ tone: 'idle', message: '' });
    try {
      const result = await discoverModelConnectionModels({
        provider: CUSTOM_AI_PROVIDER,
        apiKey,
        baseUrl,
      });

      if (!result.ok) {
        setConnectionState({ tone: 'error', message: result.message || t('Model discovery failed') });
        return;
      }

      const discoveredModels = result.models
        .map((modelId) => ({
          id: generateEntryId(),
          modelId,
          displayName: '',
          enabled: true,
          source: 'fetched' as const,
        }));

      if (discoveredModels.length > 0) {
        setDiscoveredModels(discoveredModels);
      }

      setConnectionState({
        tone: 'success',
        message: discoveredModels.length > 0
          ? result.message
          : t('No new models found.'),
      });
    } catch (error) {
      setConnectionState({
        tone: 'error',
        message: error instanceof Error ? error.message : t('Model discovery failed'),
      });
    } finally {
      setIsFetchingModels(false);
    }
  };

  if (!isOpen) return null;

  const isCustomProvider = provider === CUSTOM_AI_PROVIDER;
  const providerLabel = isCustomProvider
    ? activeCustomConnection?.name.trim() || effectiveProviders.find((p) => p.value === provider)?.label || provider
    : effectiveProviders.find((p) => p.value === provider)?.label || provider;
  const availableModels = effectiveModels.filter((m) => m.provider === provider);
  const activeProviderLabel = providerLabel;
  const activeModelLabel = isCustomProvider
    ? activeCustomModel?.displayName?.trim() || activeCustomModel?.modelId.trim() || t('Model Name')
    : availableModels.find((m) => m.value === model)?.label || model;
  const accountLabel = authUser?.username || authUser?.email || authUser?.name || authUser?.phone || t('Authenticated User');
  const verifiedAccountName = authUser?.username || (authUser?.name && !isEmailLike(authUser.name) && !isPlanLikeName(authUser.name)
    ? authUser.name
    : undefined);
  const accountRows = [
    { label: t('Username'), value: verifiedAccountName },
    { label: t('Email'), value: authUser?.email },
    { label: t('Phone'), value: authUser?.phone },
    { label: t('Role'), value: getRoleLabel(authUser?.role, t) },
  ];
  const apiKeyPlaceholder = language === 'Chinese'
    ? `请输入 ${providerLabel} API Key`
    : `Enter ${providerLabel} API Key`;
  const baseUrlPlaceholder = isCustomProvider
    ? 'https://api.example.com/v1'
    : effectiveProviders.find((p) => p.value === provider)?.baseUrl || t('Custom Base URL');
  const connectionMessageClass = connectionState.tone === 'success'
    ? 'text-emerald-600 dark:text-emerald-400'
    : connectionState.tone === 'error'
      ? 'text-red-600 dark:text-red-400'
      : 'text-zinc-500 dark:text-zinc-400';
  const sections: Array<{
    id: SettingsSectionId;
    title: string;
    description: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
  }> = [
    {
      id: 'profile',
      title: t('Account and Profile'),
      description: accountLabel,
      icon: User,
    },
    {
      id: 'ai',
      title: t('AI Provider Settings'),
      description: activeModelLabel ? `${activeProviderLabel} / ${activeModelLabel}` : activeProviderLabel,
      icon: Key,
    },
    {
      id: 'prompting',
      title: t('Advanced Prompting'),
      description: customInstructions.trim() ? t('Custom instructions enabled') : t('Using default instructions'),
      icon: MessageSquareCode,
    },
    {
      id: 'agent',
      title: t('RealPLC Agent'),
      description: `${tiaAgentEnabled ? 'TIA ✓' : 'TIA —'} / ${codesysAgentEnabled ? 'CODESYS ✓' : 'CODESYS —'}`,
      icon: PlugZap,
    },
    {
      id: 'preferences',
      title: t('Preferences'),
      description: `${t(theme)} / ${t(uiLanguage)}`,
      icon: SlidersHorizontal,
    },
  ];
  const activeSectionMeta = sections.find((section) => section.id === activeSection) ?? sections[0];
  const ActiveSectionIcon = activeSectionMeta.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--color-modal-overlay)] backdrop-blur-sm">
      <div className="mx-4 flex h-[85vh] max-h-[900px] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-modal)] shadow-[var(--shadow-modal)]">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 pb-4 pt-6">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] md:text-xl">{t('Settings')}</h2>
          <button onClick={onClose} className="text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-secondary)]">
            <X size={20} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <aside className="border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] lg:w-72 lg:border-b-0 lg:border-r">
            <div className="custom-scrollbar flex gap-2 overflow-x-auto px-4 py-4 lg:flex-col lg:overflow-visible lg:px-4 lg:py-5">
              {sections.map(({ id, title, description, icon: Icon }) => {
                const isActive = id === activeSection;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveSection(id)}
                    className={`flex min-w-fit items-center gap-3 overflow-hidden rounded-lg border px-3 py-3 text-left text-sm transition-colors lg:w-full lg:min-w-0 ${
                      isActive
                        ? 'border-[var(--color-brand-subtle-border)] bg-[var(--color-brand-subtle)] text-[var(--color-text-primary)]'
                        : 'border-transparent bg-transparent text-[var(--color-text-secondary)] hover:border-[var(--color-border)] hover:bg-[var(--color-bg-card)]'
                    }`}
                  >
                    <Icon size={18} className={`${isActive ? 'text-[var(--color-brand)]' : 'text-[var(--color-text-muted)]'} shrink-0`} />
                    <span className="min-w-0 flex-1">
                      <span className="block whitespace-nowrap lg:whitespace-normal">{title}</span>
                      <span className={`mt-1 block truncate text-xs ${isActive ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-muted)]'}`}>
                        {description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="border-b border-[var(--color-border)] px-6 pb-3 pt-5">
              <div className="flex items-center gap-3">
                <ActiveSectionIcon size={18} className="text-[var(--color-brand)]" />
                <h3 className="text-base font-semibold text-[var(--color-text-primary)] md:text-lg">
                  {activeSectionMeta.title}
                </h3>
              </div>
            </div>
            <div className="custom-scrollbar flex-1 overflow-y-auto px-6 py-5">
              {activeSection === 'profile' ? (
                <div className="space-y-6">
                  <section className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Personal display')}</h4>
                      <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('Used for your avatar, sidebar, and collaboration identity.')}</p>
                    </div>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="relative w-fit">
                        {userAvatar ? (
                          <img
                            src={userAvatar}
                            alt={t('Avatar')}
                            className="h-20 w-20 rounded-lg border-2 border-cyan-500/30 object-cover"
                          />
                        ) : (
                          <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-cyan-500/30 bg-gradient-to-tr from-cyan-600 to-blue-600 text-white">
                            <User size={36} />
                          </div>
                        )}
                        <label className="absolute -bottom-1 -right-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-zinc-200 bg-zinc-900 transition-colors hover:bg-zinc-800 dark:border-zinc-800 dark:bg-zinc-100 dark:hover:bg-white">
                          <Camera size={14} className="text-white dark:text-zinc-900" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setUserAvatar(reader.result as string);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      </div>

                      <div className="grid flex-1 gap-4">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">{t('Display Name')}</label>
                          <input
                            type="text"
                            value={userName}
                            onChange={(event) => setUserName(event.target.value)}
                            placeholder={t('Display name placeholder')}
                            className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 dark:placeholder:text-zinc-600"
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-4 dark:border-zinc-800 dark:bg-[#151515]">
                    <div className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
                      {t('Signed in as')}
                    </div>
                    <div className="mt-2 text-base font-semibold text-zinc-900 dark:text-zinc-100">
                      {accountLabel}
                    </div>
                    <div className="mt-4 grid gap-3 text-sm sm:grid-cols-4">
                      {accountRows.map((row) => (
                        <div key={row.label} className="min-w-0">
                          <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{row.label}</div>
                          <div className="mt-1 truncate text-zinc-800 dark:text-zinc-200">
                            {row.value || '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Account security')}</h4>
                      <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                        {t('Use account security to update your username, profile, or password.')}
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                      onClick={() => onOpenAccount?.('profile')}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                      >
                      <UserCog size={16} />
                      {t('Manage account details')}
                    </button>
                    <button
                      type="button"
                      onClick={() => onOpenAccount?.('security')}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                      >
                      <Lock size={16} />
                      {t('Change password')}
                    </button>
                    </div>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {t('Administrators can reset passwords, but cannot view passwords after users change them.')}
                    </p>
                    {onLogout ? (
                      <button
                        type="button"
                        onClick={onLogout}
                        className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                      >
                        <LogOut size={16} />
                        {t('Sign out now')}
                      </button>
                    ) : null}
                  </section>
                </div>
              ) : null}

              {activeSection === 'ai' ? (
                <div className="space-y-5">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400 md:text-base">{t('Active Provider')}</label>
                    <select
                      value={provider}
                      onChange={(event) => {
                        const nextProvider = event.target.value;
                        setProvider(nextProvider);
                        const firstModel = effectiveModels.find((m) => m.provider === nextProvider);
                        if (nextProvider === CUSTOM_AI_PROVIDER) {
                          if (customConnections.length === 0) {
                            const connection = createBlankCustomConnection();
                            const firstCustomModel = connection.models[0];
                            setCustomConnections([connection]);
                            setSelectedCustomConnectionId(connection.id);
                            setSelectedCustomModelId(firstCustomModel.id);
                            setModel(firstCustomModel.id);
                          } else {
                            const connection = activeCustomConnection || customConnections[0];
                            const customModel = activeCustomModel || connection.models[0];
                            setSelectedCustomConnectionId(connection.id);
                            setSelectedCustomModelId(customModel.id);
                            setModel(customModel.id);
                          }
                        } else if (firstModel) {
                          setModel(firstModel.value);
                        }
                        setSettingsError('');
                        setConnectionState({ tone: 'idle', message: '' });
                      }}
                      className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 md:text-base"
                    >
                      {effectiveProviders.map((item) => (
                        <option key={item.value} value={item.value}>{t(item.label)}{item.visible === false ? ` — ${t('Hidden')}` : ''}</option>
                      ))}
                    </select>
                  </div>

                  {isCustomProvider ? (
                    <>
                      <div className="space-y-2">
                        <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400 md:text-base">{t('Custom provider connections')}</label>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <select
                            value={activeCustomConnection?.id || ''}
                            onChange={(event) => {
                              const nextConnection = customConnections.find((connection) => connection.id === event.target.value);
                              const nextModel = nextConnection?.models[0];
                              setSelectedCustomConnectionId(event.target.value);
                              if (nextModel) {
                                setSelectedCustomModelId(nextModel.id);
                                setModel(nextModel.id);
                              }
                              setSettingsError('');
                              setConnectionState({ tone: 'idle', message: '' });
                              setDiscoveredModels([]);
                            }}
                            className="min-w-0 flex-1 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 md:text-base"
                          >
                            {customConnections.map((connection, index) => (
                              <option key={connection.id} value={connection.id}>
                                {formatCustomConnectionLabel(connection, `${t('Custom Model')} ${index + 1}`)}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleAddCustomConnection}
                              className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                            >
                              <Plus size={15} />
                              {t('Add')}
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteCustomConnection}
                              disabled={!activeCustomConnection}
                              className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                              title={t('Remove')}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400 md:text-base">{t('Model Provider Name')}</label>
                        <input
                          type="text"
                          value={activeCustomConnection?.name || ''}
                          onChange={(event) => updateActiveCustomConnection({ name: event.target.value })}
                          placeholder={t('OpenAI-compatible provider')}
                          list="custom-provider-name-list"
                          className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 dark:placeholder:text-zinc-600 md:text-base"
                        />
                        {providerNameHistory.length > 0 && (
                          <datalist id="custom-provider-name-list">
                            {providerNameHistory.map((name) => (
                              <option key={name} value={name} />
                            ))}
                          </datalist>
                        )}
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">
                          {t('Base URL')}
                        </label>
                        <input
                          type="text"
                          value={activeCustomConnection?.baseUrl || ''}
                          onChange={(event) => updateActiveCustomConnection({ baseUrl: event.target.value })}
                          placeholder="https://api.example.com/v1"
                          className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 dark:placeholder:text-zinc-600 md:text-base"
                        />
                      </div>
                      <div>
                        <label className="mb-1 flex items-center gap-1 text-sm font-medium text-zinc-600 dark:text-zinc-400">
                          <Key size={14} /> {(activeCustomConnection?.name?.trim() || t('Custom Provider'))} {t('API Keys')}
                        </label>
                        <input
                          type="password"
                          value={activeCustomConnection?.apiKey || ''}
                          onChange={(event) => updateActiveCustomConnection({ apiKey: event.target.value })}
                          placeholder={apiKeyPlaceholder}
                          className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 dark:placeholder:text-zinc-600"
                        />
                        <p className="mt-1 break-words text-[10px] text-zinc-400 dark:text-zinc-500">{t('Keys are stored locally in your browser.')}</p>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <button
                          type="button"
                          onClick={handleFetchCustomModels}
                          disabled={isFetchingModels}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                        >
                          {isFetchingModels ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                          {isFetchingModels ? t('Fetching models...') : t('Fetch Models')}
                        </button>
                        <button
                          type="button"
                          onClick={handleTestConnection}
                          disabled={isTestingConnection}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                        >
                          <Key size={15} />
                          {isTestingConnection ? t('Testing connection...') : t('Test Connection')}
                        </button>
                        {connectionState.message ? (
                          <span className={`text-xs ${connectionMessageClass}`}>{connectionState.message}</span>
                        ) : null}
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400 md:text-base">{t('Model Name (OpenAI format)')}</label>
                        <input
                          type="text"
                          value={activeCustomModel?.modelId || ''}
                          onChange={(event) => {
                            updateActiveCustomModel({ modelId: event.target.value });
                            if (activeCustomModel) setModel(activeCustomModel.id);
                          }}
                          placeholder={t('gpt-4o-mini, deepseek-chat, qwen-plus')}
                          className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 dark:placeholder:text-zinc-600 md:text-base"
                        />
                        <p className="mt-1 break-words text-[10px] text-zinc-400 dark:text-zinc-500">{t('Use the exact model name accepted by the provider API.')}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400 md:text-base">{t('Models in this provider')}</label>
                        <div
                          ref={modelListRef}
                          role="listbox"
                          aria-label={t('Models in this provider')}
                          className="mb-2 max-h-[200px] overflow-y-auto rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-[#151515]"
                        >
                          {activeCustomConnection?.models.map((item) => {
                            const isSelected = item.id === activeCustomModel?.id;
                            return (
                              <div
                                key={item.id}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => {
                                  setSelectedCustomModelId(item.id);
                                  setModel(item.id);
                                  setSettingsError('');
                                  setConnectionState({ tone: 'idle', message: '' });
                                }}
                                className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors border-b border-zinc-100 dark:border-zinc-800 last:border-b-0 ${
                                  isSelected
                                    ? 'bg-cyan-50 text-cyan-800 dark:bg-cyan-500/10 dark:text-cyan-200'
                                    : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800'
                                }`}
                              >
                                <span className="truncate text-sm flex-1">
                                  {item.modelId || t('New Model')}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleModelEnabled(item.id);
                                  }}
                                  aria-label={item.enabled ? t('Disable') : t('Enable')}
                                  className={`relative ml-2 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                                    item.enabled
                                      ? 'bg-emerald-500 dark:bg-emerald-400'
                                      : 'bg-zinc-300 dark:bg-zinc-600'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                      item.enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                    }`}
                                  />
                                </button>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteCustomModel(item.id);
                                  }}
                                  className="ml-2 flex-shrink-0 rounded p-1 text-zinc-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:text-zinc-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                                  title={t('Remove')}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            );
                          })}
                          {(!activeCustomConnection?.models || activeCustomConnection.models.length === 0) && (
                            <div className="px-3 py-4 text-center text-sm text-zinc-400 dark:text-zinc-500">
                              {t('No models added yet.')}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleAddCustomModel}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                        >
                          <Plus size={15} />
                          {t('Add Model')}
                        </button>
                      </div>
                      {discoveredModels.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400">
                              {t('Discovered Models')}
                            </label>
                            <button
                              type="button"
                              onClick={handleAddAllDiscoveredModels}
                              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 dark:text-cyan-300 dark:bg-cyan-500/10 dark:hover:bg-cyan-500/20 transition-colors"
                            >
                              <Plus size={12} />
                              {t('Add All')}
                            </button>
                          </div>
                          <div className="rounded-lg border border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-[#151515]">
                            {discoveredModels.map((model) => (
                              <div
                                key={model.id}
                                className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 last:border-b-0"
                              >
                                <span className="truncate text-sm text-zinc-700 dark:text-zinc-300">
                                  {model.modelId}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleAddDiscoveredModel(model)}
                                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 dark:text-cyan-300 dark:bg-cyan-500/10 dark:hover:bg-cyan-500/20 transition-colors"
                                >
                                  <Plus size={12} />
                                  {t('Add')}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {settingsError ? (
                        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                          {settingsError}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400 md:text-base">{t('Model')}</label>
                        <select
                          value={model}
                          onChange={(event) => {
                            setModel(event.target.value);
                            setConnectionState({ tone: 'idle', message: '' });
                          }}
                          className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 md:text-base"
                        >
                          {availableModels.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}{item.visible === false ? ` — ${t('Hidden')}` : ''}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 flex items-center gap-1 text-sm font-medium text-zinc-600 dark:text-zinc-400">
                          <Key size={14} /> {providerLabel} {t('API Keys')}
                        </label>
                        <input
                          type="password"
                          value={apiKeys[provider] || ''}
                          onChange={(event) => handleKeyChange(provider, event.target.value)}
                          placeholder={apiKeyPlaceholder}
                          className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 dark:placeholder:text-zinc-600"
                        />
                        <p className="mt-1 break-words text-[10px] text-zinc-400 dark:text-zinc-500">{t('Keys are stored locally in your browser.')}</p>
                      </div>
                      {provider !== 'google' ? (
                        <div>
                          <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">
                            {t('Base URLs (Optional)')}
                          </label>
                          <input
                            type="text"
                            value={baseUrls[provider] || ''}
                            onChange={(event) => handleUrlChange(provider, event.target.value)}
                            placeholder={baseUrlPlaceholder}
                            className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600 dark:placeholder:text-zinc-600"
                          />
                        </div>
                      ) : null}
                      {settingsError ? (
                        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                          {settingsError}
                        </p>
                      ) : null}
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <button
                          type="button"
                          onClick={handleTestConnection}
                          disabled={isTestingConnection}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                        >
                          <Key size={15} />
                          {isTestingConnection ? t('Testing connection...') : t('Test Connection')}
                        </button>
                        {connectionState.message ? (
                          <span className={`text-xs ${connectionMessageClass}`}>{connectionState.message}</span>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              ) : null}

              {activeSection === 'prompting' ? (
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      {t('Custom System Instructions (Naming conventions, safety rules, etc.)')}
                    </label>
                    <textarea
                      value={customInstructions}
                      onChange={(event) => setCustomInstructions(event.target.value)}
                      maxLength={MAX_CUSTOM_INSTRUCTION_CHARS}
                      placeholder={t("Example custom instructions")}
                      className="custom-scrollbar min-h-[240px] w-full resize-y rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300"
                    />
                    <p className="mt-2 break-words text-[10px] text-zinc-400 dark:text-zinc-500">
                      {t('These instructions are prepended to every AI generation request to influence naming, logic, or coding style.')}
                    </p>
                  </div>
                </div>
              ) : null}

              {activeSection === 'agent' ? (
                <div className="space-y-5">
                  <section className="rounded-xl border border-cyan-200 bg-cyan-50/60 p-4 dark:border-cyan-500/30 dark:bg-cyan-500/10">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <PlugZap size={18} className="text-cyan-700 dark:text-cyan-300" />
                          <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">RealPLC Agent Core</h4>
                        </div>
                        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                          {t('One host connection manages independently packaged engineering connectors in the active Windows user session.')}
                        </p>
                      </div>
                      <div className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        agentIntegrationStatus?.connectionState === 'online'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                          : 'bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'
                      }`}>
                        {agentIntegrationLoading
                          ? t('Detecting')
                          : agentIntegrationStatus?.connectionState === 'online' ? t('Online') : t('Offline')}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 text-xs sm:grid-cols-3">
                      <div><span className="text-zinc-500">{t('Version')}: </span>{agentIntegrationStatus?.coreVersion ?? '-'}</div>
                      <div><span className="text-zinc-500">{t('Session')}: </span>{t('Single active user')}</div>
                      <div><span className="text-zinc-500">{t('Agent')}: </span>{agentIntegrationStatus?.agentName ?? '-'}</div>
                    </div>
                    {agentIntegrationError ? <p className="mt-3 text-xs text-red-600 dark:text-red-400">{agentIntegrationError}</p> : null}
                  </section>
                  <section className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-4 dark:border-zinc-800 dark:bg-[#151515]">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <PlugZap size={17} className="text-cyan-600 dark:text-cyan-400" />
                          <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Connector for Siemens TIA Portal')}</h4>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                          {t('Independent Siemens connector for environment checks and SCL sandbox validation.')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTiaAgentEnabled((enabled) => !enabled)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                          tiaAgentEnabled ? 'bg-cyan-600' : 'bg-zinc-300 dark:bg-zinc-600'
                        }`}
                        aria-pressed={tiaAgentEnabled}
                        aria-label={t('Toggle TIA Agent')}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            tiaAgentEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Status')}</div>
                        <div className={`mt-1 font-semibold ${agentIntegrationStatus?.connectors.find(item => item.connectorId === 'tia')?.ready ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {connectorStatusLabel(agentIntegrationStatus?.connectors.find(item => item.connectorId === 'tia'), t)}
                        </div>
                      </div>
                      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Platform')}</div>
                        <div className="mt-1 font-semibold text-zinc-900 dark:text-zinc-100">{connectorProductLabel(agentIntegrationStatus?.connectors.find(item => item.connectorId === 'tia'), 'Siemens / SCL')}</div>
                      </div>
                      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Safety')}</div>
                        <div className="mt-1 font-semibold text-zinc-900 dark:text-zinc-100">{t('Sandbox validation')}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        requestOpenTiaAgentSettings();
                      }}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg border border-cyan-200 bg-white px-3 py-2 text-sm font-medium text-cyan-700 transition-colors hover:bg-cyan-50 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-200"
                    >
                      <ServerCog size={15} />
                      {t('Open TIA connector settings')}
                    </button>
                  </section>

                  <section className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-4 dark:border-zinc-800 dark:bg-[#151515]">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Cpu size={17} className="text-cyan-600 dark:text-cyan-400" />
                          <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Connector for CODESYS</h4>
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                          {t('Independent CODESYS connector, ScriptEngine checks, and native ST closed-loop validation.')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCodesysAgentEnabled(enabled => !enabled)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                          codesysAgentEnabled ? 'bg-cyan-600' : 'bg-zinc-300 dark:bg-zinc-600'
                        }`}
                        aria-pressed={codesysAgentEnabled}
                        aria-label={t('Toggle RealPLC CODESYS Agent')}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            codesysAgentEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Status')}</div>
                        <div className={`mt-1 font-semibold ${agentIntegrationStatus?.connectors.find(item => item.connectorId === 'codesys')?.ready ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {connectorStatusLabel(agentIntegrationStatus?.connectors.find(item => item.connectorId === 'codesys'), t)}
                        </div>
                      </div>
                      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Platform')}</div>
                        <div className="mt-1 font-semibold text-zinc-900 dark:text-zinc-100">{connectorProductLabel(agentIntegrationStatus?.connectors.find(item => item.connectorId === 'codesys'), 'CODESYS / ST')}</div>
                      </div>
                      <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Safety')}</div>
                        <div className="mt-1 font-semibold text-zinc-900 dark:text-zinc-100">{t('Independent sandbox')}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        requestOpenCodesysAgentSettings();
                      }}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg border border-cyan-200 bg-white px-3 py-2 text-sm font-medium text-cyan-700 transition-colors hover:bg-cyan-50 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-200"
                    >
                      <Cpu size={15} />
                      {t('Open CODESYS connector settings')}
                    </button>
                  </section>
                </div>
              ) : null}

              {activeSection === 'preferences' ? (
                <div className="space-y-5">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">{t('Theme')}</label>
                    <select
                      value={theme}
                      onChange={(event) => setTheme(event.target.value)}
                      className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600"
                    >
                      <option value="Dark">{t('Dark')}</option>
                      <option value="Light">{t('Light')}</option>
                      <option value="System">{t('System')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">{t('Default Language')}</label>
                    <select
                      value={uiLanguage}
                      onChange={(event) => setUiLanguage(event.target.value)}
                      className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600"
                    >
                      <option value="System">{t('System')}</option>
                      <option value="English">{t('English')}</option>
                      <option value="Chinese">{t('Chinese')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">{t('Plan auto-approval')}</label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setPlanAutoApprovalEnabled(!planAutoApprovalEnabled)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                          planAutoApprovalEnabled ? 'bg-cyan-600' : 'bg-zinc-300 dark:bg-zinc-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            planAutoApprovalEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">
                        {planAutoApprovalEnabled ? t('Enabled') : t('Disabled')}
                      </span>
                    </div>
                  </div>

                  {planAutoApprovalEnabled && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-zinc-600 dark:text-zinc-400">{t('Countdown seconds')}</label>
                      <select
                        value={planAutoApprovalSeconds}
                        onChange={(event) => setPlanAutoApprovalSeconds(Number(event.target.value))}
                        className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 transition-colors focus:border-cyan-500 focus:outline-none dark:border-zinc-800 dark:bg-[#151515] dark:text-zinc-300 dark:focus:border-zinc-600"
                      >
                        <option value={3}>3s</option>
                        <option value={5}>5s</option>
                        <option value={10}>10s</option>
                      </select>
                    </div>
                  )}
                </div>
              ) : null}

            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-[var(--color-border)] px-6 py-4">
          <button onClick={onClose} className="rounded-lg bg-transparent px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-surface)] md:text-base">
            {t('Cancel')}
          </button>
          <button onClick={handleSave} className="rounded-lg bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-hover)] md:text-base">
            {t('Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
};
